import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiPost } from '../../services/api';
import CountrySearchSelect from '../common/CountrySearchSelect';
import { useCountries } from '../../hooks/useCountries';
import {
  COMPANY_DASHBOARD_PATH,
  fetchCompanyOnboardingStatus,
} from '../../utils/companyRouting';

const DESCRIPTION_MIN = 50;
const DESCRIPTION_MAX = 500;

export default function CompanyOnboarding() {
  const { user, token, authLoading } = useAuth();
  const navigate = useNavigate();
  const { countries, loading: countriesLoading } = useCountries();

  const [checking, setChecking] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [vatId, setVatId] = useState('');
  const [vatCountry, setVatCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [companyTermsAccepted, setCompanyTermsAccepted] = useState(false);
  const [companyTermsError, setCompanyTermsError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (authLoading) return;

    if (!token || !user) {
      navigate('/login', { replace: true });
      return;
    }

    if (user.role !== 'company') {
      navigate('/dashboard', { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      const status = await fetchCompanyOnboardingStatus(token);
      if (cancelled) return;
      if (status?.has_company) {
        navigate(COMPANY_DASHBOARD_PATH, { replace: true });
        return;
      }
      setChecking(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, token, user, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setCompanyTermsError(false);
    setSuccessMessage('');

    if (!companyTermsAccepted) {
      setCompanyTermsError(true);
      return;
    }

    if (description.length < DESCRIPTION_MIN) {
      setFieldErrors((prev) => ({
        ...prev,
        description: [`Description must be at least ${DESCRIPTION_MIN} characters.`],
      }));
      return;
    }

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        company_name: companyName.trim(),
        legal_name: legalName.trim(),
        website: website.trim(),
        country: country.trim(),
        city: city.trim(),
        address: address.trim(),
        description: description.trim(),
        phone: phone.trim() || null,
        vat_id: vatId.trim() || null,
        vat_country: vatId.trim() ? vatCountry.trim().toUpperCase() : null,
        company_terms_accepted: companyTermsAccepted,
      };

      const res = await apiPost('/api/v1/company/onboarding', body, token);
      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      if (res.status === 403) {
        setError('Access denied.');
        return;
      }
      if (res.status === 422 && data.errors) {
        setFieldErrors(data.errors);
        return;
      }
      if (!res.ok) {
        setError(data.message || 'Something went wrong. Please try again.');
        return;
      }

      setSuccessMessage('Company profile created! Welcome to RemoteWorkMarket.');
      window.setTimeout(() => {
        navigate(COMPANY_DASHBOARD_PATH, { replace: true });
      }, 1200);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading || checking) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" data-testid="rwm-onboarding-loading">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }

  const descriptionCount = description.length;

  return (
    <Container className="py-4 rwm-onboarding">
      <Row className="justify-content-center">
        <Col lg={8} md={10}>
          <div className="rwm-onboarding__header text-center mb-4">
            <h1 className="h3 rwm-onboarding__title">Set Up Your Company Profile</h1>
            <p className="text-muted mb-0 rwm-onboarding__subtitle">
              This takes 2 minutes. You only do this once.
            </p>
          </div>

          {successMessage ? (
            <Alert variant="success" data-testid="rwm-onboarding-success">
              {successMessage}
            </Alert>
          ) : null}

          {error ? (
            <Alert variant="danger" data-testid="rwm-onboarding-error">
              {error}
            </Alert>
          ) : null}

          <Card className="rwm-onboarding__card border">
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit} data-testid="rwm-onboarding-form" noValidate>
                <Form.Group className="mb-3" controlId="onboardingCompanyName">
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    value={companyName}
                    onChange={(ev) => setCompanyName(ev.target.value)}
                    required
                    maxLength={255}
                    data-testid="rwm-onboarding-company_name"
                  />
                  {fieldErrors.company_name ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.company_name[0]}</div>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3" controlId="onboardingLegalName">
                  <Form.Label>Legal Name *</Form.Label>
                  <Form.Text className="d-block text-muted mb-1">
                    Your registered business name
                  </Form.Text>
                  <Form.Control
                    value={legalName}
                    onChange={(ev) => setLegalName(ev.target.value)}
                    required
                    maxLength={255}
                    data-testid="rwm-onboarding-legal_name"
                  />
                  {fieldErrors.legal_name ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.legal_name[0]}</div>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3" controlId="onboardingWebsite">
                  <Form.Label>Website *</Form.Label>
                  <Form.Control
                    type="url"
                    value={website}
                    onChange={(ev) => setWebsite(ev.target.value)}
                    required
                    maxLength={255}
                    placeholder="https://yourcompany.com"
                    data-testid="rwm-onboarding-website"
                  />
                  {fieldErrors.website ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.website[0]}</div>
                  ) : null}
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="onboardingCountry">
                      <Form.Label>Country *</Form.Label>
                      <CountrySearchSelect
                        countries={countries}
                        loading={countriesLoading}
                        value={country}
                        onChange={setCountry}
                        invalid={Boolean(fieldErrors.country)}
                      />
                      {fieldErrors.country ? (
                        <div className="rwm-auth__field-error mt-1">{fieldErrors.country[0]}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="onboardingCity">
                      <Form.Label>City *</Form.Label>
                      <Form.Control
                        value={city}
                        onChange={(ev) => setCity(ev.target.value)}
                        required
                        maxLength={100}
                        data-testid="rwm-onboarding-city"
                      />
                      {fieldErrors.city ? (
                        <div className="rwm-auth__field-error mt-1">{fieldErrors.city[0]}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="onboardingAddress">
                  <Form.Label>Address *</Form.Label>
                  <Form.Control
                    value={address}
                    onChange={(ev) => setAddress(ev.target.value)}
                    required
                    maxLength={255}
                    data-testid="rwm-onboarding-address"
                  />
                  {fieldErrors.address ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.address[0]}</div>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3" controlId="onboardingDescription">
                  <Form.Label>Company Description *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={description}
                    onChange={(ev) => setDescription(ev.target.value)}
                    required
                    minLength={DESCRIPTION_MIN}
                    maxLength={DESCRIPTION_MAX}
                    data-testid="rwm-onboarding-description"
                  />
                  <div
                    className={`small mt-1 ${
                      descriptionCount < DESCRIPTION_MIN ? 'text-danger' : 'text-muted'
                    }`}
                    data-testid="rwm-onboarding-description-count"
                  >
                    {descriptionCount} / {DESCRIPTION_MAX} characters
                    {descriptionCount < DESCRIPTION_MIN
                      ? ` (minimum ${DESCRIPTION_MIN})`
                      : null}
                  </div>
                  {fieldErrors.description ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.description[0]}</div>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3" controlId="onboardingVatId">
                  <Form.Label>VAT ID</Form.Label>
                  <Form.Text className="d-block text-muted mb-1">
                    EU companies only. Leave blank if not applicable.
                  </Form.Text>
                  <Form.Control
                    value={vatId}
                    onChange={(ev) => setVatId(ev.target.value)}
                    maxLength={50}
                    data-testid="rwm-onboarding-vat_id"
                  />
                  {fieldErrors.vat_id ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.vat_id[0]}</div>
                  ) : null}
                </Form.Group>

                {vatId.trim() ? (
                  <Form.Group className="mb-3" controlId="onboardingVatCountry">
                    <Form.Label>VAT Country</Form.Label>
                    <Form.Control
                      value={vatCountry}
                      onChange={(ev) => setVatCountry(ev.target.value.toUpperCase())}
                      maxLength={2}
                      placeholder="NL"
                      data-testid="rwm-onboarding-vat_country"
                    />
                    {fieldErrors.vat_country ? (
                      <div className="rwm-auth__field-error mt-1">{fieldErrors.vat_country[0]}</div>
                    ) : null}
                  </Form.Group>
                ) : null}

                <Form.Group className="mb-4" controlId="onboardingPhone">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={phone}
                    onChange={(ev) => setPhone(ev.target.value)}
                    maxLength={30}
                    data-testid="rwm-onboarding-phone"
                  />
                  {fieldErrors.phone ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.phone[0]}</div>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3" controlId="onboardingCompanyTerms">
                  <Form.Check
                    type="checkbox"
                    id="onboardingCompanyTermsCheck"
                    checked={companyTermsAccepted}
                    onChange={(e) => {
                      setCompanyTermsAccepted(e.target.checked);
                      setCompanyTermsError(false);
                    }}
                    data-testid="rwm-onboarding-company-terms"
                    label={
                      <span>
                        I have read and agree to the{' '}
                        <Link to="/terms" target="_blank" rel="noopener noreferrer">
                          Company Terms &amp; Conditions
                        </Link>{' '}
                        — including anti-ghosting obligations, reactivation fee policy, and listing
                        rejection policy.
                      </span>
                    }
                  />
                  {companyTermsError || fieldErrors.company_terms_accepted ? (
                    <div
                      className="rwm-auth__field-error mt-1"
                      data-testid="rwm-onboarding-company-terms-error"
                    >
                      {fieldErrors.company_terms_accepted?.[0] ||
                        'You must accept the Company Terms & Conditions.'}
                    </div>
                  ) : null}
                </Form.Group>

                <Button
                  type="submit"
                  variant="info"
                  className="w-100 text-white btn-lg"
                  disabled={submitting}
                  data-testid="rwm-onboarding-submit"
                >
                  {submitting ? 'Saving…' : 'Complete Setup'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
